import { useState, useEffect, useCallback } from 'react';
import Display from '../Display';
import Chat from '../Chat';
import ChatInput from '../../ChatInput/ChatInput';
import { useChatbotStore, useChatStateStore, useMessagesStore, useProductsStore, useUserStore, useToastStore } from '../../zustand/store';
import { useChatbot, useHistoryUpdate, useLoadHistory, useSendPDPMessage, useSendMessageWithWebSocket } from '../../services/queries/chatbotQueries';
import { setColorCode } from '../../utils/utils';
import { useParams, useSearchParams } from 'react-router-dom';
import { sendInputFocusStatus } from '../../utils/utils';
import { handleConsoleTourMessage } from '../../utils/consoleTourHandler';
import { LayoutModeEnum, EnableModeEnum, DisplayContentModeEnum } from '../../type/modeState.type';
import { useAgentSocketStore } from '../../zustand/agentSocketStore';
import { StreamView } from '../ChatModal/StreamView';

const ChatFieldBase = (props) => {
    const { chatbot, setBootConfigGreetingComment } = useChatbotStore();
    const { products } = useProductsStore();
    const { 
        isPreAnswered, 
        displayLocation, 
        setIsNewProduct, 
        setIsChatbotPending,
        setEnableMode, 
        setIsChatOpen,
        setIsStreaming, 
        currentPage, 
        setIsInConsoleTour, 
        layoutMode, 
        enableMode 
    } = useChatStateStore();
    const { 
        partnerType, 
        chatUserId, 
        partnerId,
        setIsMobileDevice, 
    } = useUserStore();
    const { setCustomizedGreeting } = useChatStateStore(); // 🧪 맞춤형 그리팅 설정 함수
    const { setToastMessage, setIsToastVisible } = useToastStore();
    const { refetch: refetchLoadHistory } = useLoadHistory();
    const [mode, setMode] = useState(DisplayContentModeEnum.GREETING);
    const [isDisplayVisible, setIsDisplayVisible] = useState(false);
    const queryString = window.location.search;
    const [searchParams, setSearchParams] = new useSearchParams(queryString);
    const chatBotId = useParams().chatBotId;
    const lyclePartnerId = searchParams.get('ptid');
    const { refetch: refetchChatbot } = useChatbot(lyclePartnerId);
    const { mutate: sendPDPMessage } = useSendPDPMessage();
    const { mutateAsync: updateHistory } = useHistoryUpdate();
    const { messages, streamMessage, setGuidelineContent, setGuidelineTitle, setGuidelineId } = useMessagesStore();
    const [visitedState, setVisitedState] = useState(null);
    const { mutate: sendMessageWithWebSocket } = useSendMessageWithWebSocket();

    useEffect(() => {
        const messageExistence = {
            type: 'messageExistence',
            messageExistenceState: messages.length > 0,
        }
        window.parent.postMessage(messageExistence, '*');
    }, [messages]);

    const handleConsoleTourMessageWrapper = (e) => {
        const { clearExamples } = useProductsStore.getState();
        handleConsoleTourMessage(e, {
            setIsStreaming,
            setIsChatbotPending,
            sendInputFocusStatus,
            currentPage,
            setIsInConsoleTour,
            clearExamples,
            sendMessageWithWebSocket
        });
    };

    useEffect(() => {
        window.addEventListener('message', handleConsoleTourMessageWrapper);
        return () => {
            window.removeEventListener('message', handleConsoleTourMessageWrapper);
        };
    }, []);

    const handleMessage = useCallback((e) => {
        const { 
            currentPage, 
            buttonClickState, 
            clickedElement, 
            enableMode: receivedEnableMode, 
            addedProductToCart, 
            customizedGreeting, 
            requestMessage,
            addedProductWithOptionsToCart,
            addProductToCartFailed,
            bootConfigGreetingComment, 
            messageType,
            contentData,
        } = e.data;
        const currentEnableMode = useChatStateStore.getState().enableMode;
        
        // SDK에서 받은 문자열을 Enum으로 매핑
        const enableModeMap = {
            'full': EnableModeEnum.FULL,
            'shrink': EnableModeEnum.SHRINK,
        };
        
        const mappedEnableMode = enableModeMap[receivedEnableMode];
        
        if (mappedEnableMode && currentEnableMode !== mappedEnableMode) {
            setEnableMode(mappedEnableMode);
        }

        if (customizedGreeting) {
            setCustomizedGreeting(customizedGreeting);
        }

        if (bootConfigGreetingComment) {
            setBootConfigGreetingComment(bootConfigGreetingComment);
        }

        // carouselRedirect 이벤트 처리
        // if (clickedElement === 'carouselRedirect') {
        //     updateHistory({ url: currentPage, itemId: '' })
        //         .then((visitedState) => {
        //             setVisitedState(visitedState);
        //             if (displayLocation === 'PRODUCT_DETAIL' && visitedState) {
        //                 if (visitedState === 'FIRST_VISIT') {
        //                     setIsChatbotPending(true);
        //                     sendPDPMessage({ chatType: 'default' });
        //                 } else if (visitedState === 'REVISIT') {
        //                     setIsChatbotPending(true);
        //                     sendPDPMessage({ chatType: 'revisit' });
        //                 }
        //             }
        //         });
        // }

        // 플로팅 버튼 클릭
        if ((buttonClickState && clickedElement === 'floatingContainer') || clickedElement === 'carouselRedirect') {
            updateHistory({ url: currentPage, itemId: '' })
                .then((visitedState) => {
                    setVisitedState(visitedState);
                    if (displayLocation === 'PRODUCT_DETAIL' && visitedState) {
                        if (visitedState === 'FIRST_VISIT') {
                            setIsChatbotPending(true);
                            sendPDPMessage({ chatType: 'default' });
                        } else if (visitedState === 'REVISIT') {
                            setIsChatbotPending(true);
                            sendPDPMessage({ chatType: 'revisit' });
                        }
                    }
                });
            setIsChatOpen(true);
        }

        // 닫기 버튼 클릭
        if (buttonClickState && (clickedElement === 'closeButtonContainer' || clickedElement === 'closeActionArea')) {
            setIsNewProduct(false);
            setIsChatOpen(false);
        }

        // 장바구니 상품 담기 성공
        if (addedProductToCart) {
            setToastMessage('장바구니에 상품을 담았어요');
            setIsToastVisible(true);
        }

        // 장바구니 상품 담기 성공
        if (addedProductWithOptionsToCart) {
            setToastMessage('장바구니에 상품을 담았어요');
            setIsToastVisible(true);
        }

        // 장바구니 상품 담기 실패
        if (addProductToCartFailed) {
            setToastMessage('로그인 후 다시 시도해주세요!');
            setIsToastVisible(true);
        }

        // [modal] 메세지 전송 요청 from SDK
        if (buttonClickState && clickedElement === 'sendButton') {
            setIsStreaming(true);
            setIsChatbotPending(true);
            sendMessageWithWebSocket({ messageInput: requestMessage, isChatbot: false, preAnswers: [] });
        }

        if (messageType === 'gentoo-statics') {
            sessionStorage.setItem('gentoo-statics', JSON.stringify(contentData));
        }

    }, [displayLocation]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        }
    }, [handleMessage])

    // Ensure chatInput is functional, but note that streaming chat is currently non-operational
    useEffect(() => {
        setIsStreaming(false);
        setIsChatbotPending(false);
    }, []);

    useEffect(() => {
        if (partnerType === 'fastfive' && !isPreAnswered) {
            setIsDisplayVisible(false);
        } else if (messages.length === 0 && streamMessage.length === 0) {
            setIsDisplayVisible(false);
        } else {
            setIsDisplayVisible(true);
        }
    }, [messages, streamMessage, isPreAnswered]);

    useEffect(() => {
        refetchLoadHistory();
        setColorCode(chatbot?.colorCode);
        setIsMobileDevice(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    }, [chatbot]);

    useEffect(() => {
        if (products.length > 0) {
            if (partnerType === 'fastfive') {
                setMode(DisplayContentModeEnum.PRODUCT_VERTICAL_LIST);
            } else if (layoutMode === LayoutModeEnum.MODAL) {
                setMode(DisplayContentModeEnum.SIMPLE_LIST);
            } else {
                setMode(chatbot?.carouselType === 'single' ? 
                    DisplayContentModeEnum.PRODUCT_SINGLE : 
                    (chatbot?.carouselType === 'carousel-horizontal' ? 
                        DisplayContentModeEnum.PRODUCT_HORIZONTAL_LIST : 
                        DisplayContentModeEnum.PRODUCT_VERTICAL_LIST
                    ) || DisplayContentModeEnum.PRODUCT_SINGLE
                );
            }
        } else {
            setMode(DisplayContentModeEnum.GREETING);
        }
    }, [products, layoutMode]);
    
    useEffect(() => {
        const handler = (event) => {
            if (event.data.type === 'guidelineUpdate') {
                setGuidelineId(event.data.id);
                setGuidelineContent(event.data.content);
                setGuidelineTitle(event.data.title);
            }
        };

        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
        };
    }, []);

    const connect = useAgentSocketStore((s) => s.connect);
    
    useEffect(() => {
        connect({
            action: "register",
            chatUserId: chatUserId,
            // chatUserId: 'tTrifHIpxtzgs6HK3w8NeLa22VSU443c',
            externalKey: partnerId
        }, displayLocation);
    }, [connect,chatUserId, partnerId]);

    return (
        <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {
                layoutMode === LayoutModeEnum.MODAL && enableMode === EnableModeEnum.SHRINK ?
                <StreamView />
                /* <div style={{ width: '100%', flex: 1, position: 'relative', overflow: 'scroll', padding: '8px 0', overscrollBehavior: 'contain' }}>
                    <StreamView />
                </div> */ :
                <Chat />
            }
            {
                isDisplayVisible &&
                <Display mode={mode} />
            }
            {
                Object.keys(chatbot.preQuestion.questions).length === 0 || isPreAnswered ?
                    <ChatInput /> :
                    <div style={{ width: '100%', height: '86px' }}>
                    </div>
            }
        </div>
    );
};

export default ChatFieldBase